<script setup lang="ts">
import { getLocationBadgesFromLocations, type Location } from '~/models/Location';
import type { Filters } from '~/models/SearchFilters';
import type { HousingCard } from '~/models/HousingCard';
import type { MatchesResponse } from '~/models/Housing';
import type { LocationRegion, LocationArea } from '@/models/Location';
import { getFiltersQuery } from '~/models/SearchFilters';
import { fetchSearchData } from '~/utils/fetch-search';
import { toSeoFriendly } from '~/utils/search-seo';
import type { CityPage } from '~/modules/city-pages/types';

type QueryParamValue = string | number | null | undefined | (string | number)[];
type QueryParams = Record<string, QueryParamValue | Record<string, QueryParamValue>>;

/**
 * There are certain cities that should have a search page accessible under /[city-name] for SEO reasons.
 * The module "city-pages" knows about those cities, registers the respective routes, adds a
 * "cityPage" meta object to the route and then inserts this component into the page.
 *
 * Our job here is to handle those city pages differently than normal search pages in various aspects.
 */
const cityPage = useRouter().currentRoute.value.meta.cityPage as CityPage;

const canonicalUrl = useCanonicalUrlForSearchPages();
const locationStore = useLocationStore();
const { $setSeoMeta } = useNuxtApp();
const config = useRuntimeConfig();
const baseStore = useBaseStore();
const route = useRoute();


const { t, locale } = useI18n({
  useScope: 'local',
});

$setSeoMeta();

const queryParams = route.query;

const housingTypeMap: { [key: string]: number } = {
  [t('housing_types.apartment').toLowerCase()]: 1,
  [t('housing_types.shared_room').toLowerCase()]: 2,
  [t('housing_types.house').toLowerCase()]: 3,
};
const housingKindMap: { [key: string]: number } = {
  [t('housing_kind.rent').toLowerCase()]: 1,
  [t('housing_kind.buy').toLowerCase()]: 2,
};

const reverseHousingTypeMap: { [key: number]: string } = {
  1: t('housing_types.apartment').toLowerCase(),
  2: t('housing_types.shared_room').toLowerCase(),
  3: t('housing_types.house').toLowerCase(),
};
const reverseHousingKindMap: { [key: number]: string } = {
  1: t('housing_kind.rent').toLowerCase(),
  2: t('housing_kind.buy').toLowerCase(),
};

const title = ref('');
const housingTypeText = (route.params?.housingType as string) || t('housing_types.apartment').toLowerCase();
const housingKindText = (route.params?.housingKind as string) || t('housing_kind.rent').toLowerCase();

const housingType = ref(housingTypeMap[housingTypeText]);
const housingKind = ref(housingKindMap[housingKindText]);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const searchParams = ref(route.query as Record<string, any>);

if (cityPage) {
  searchParams.value['housingType'] = 1;
  searchParams.value['locationIds[cities]'] = cityPage.locationIds.cities.join(',');
}

const loading = ref(false);
const currentPage = ref(1);
const pageCount = ref(0);
const housingsPerPage = ref(10);
const housingsOrder = ref('standard');
const housingCards = ref<HousingCard[]>([]);
const searchCount = ref(0);
const searches = ref([]);
const users = ref([]);
const filtersOffset = ref(0);
const filtersElement = ref<HTMLElement | null>(null);

const isDesktop = useIsDesktop();

const locationTexts = ref<string[]>([]);

const filters = ref<Filters>({
  locationIds: {
    regions: searchParams.value['locationIds[regions]'] ? searchParams.value['locationIds[regions]'].split(',').map(Number) : [],
    cities: searchParams.value['locationIds[cities]'] ? searchParams.value['locationIds[cities]'].split(',').map(Number) : [],
    areas: searchParams.value['locationIds[areas]'] ? searchParams.value['locationIds[areas]'].split(',').map(Number) : [],
  },
  rentMax: searchParams.value.rentMax ? Number(searchParams.value.rentMax) : null,
  sizeMin: searchParams.value.sizeMin ? Number(searchParams.value.sizeMin) : null,
  roomsMin: searchParams.value.roomsMin ? Number(searchParams.value.roomsMin) : 1,
  housingType: Number(searchParams.value.housingType || 1),
  housingKind: Number(housingKind.value),
  housingTypeId: searchParams.value.housingTypeId ? Number(searchParams.value.housingTypeId) : null,
  properties: searchParams.value.properties ? searchParams.value.properties.split(',').map(Number) : [],
  housingProperties: searchParams.value.housingProperties ? searchParams.value.housingProperties.split(',').map(Number) : [],
  radius: searchParams.value.radius ? Number(searchParams.value.radius) : null,
  storeyMin: searchParams.value.storeyMin ? Number(searchParams.value.storeyMin) : -1,
  storeyMax: searchParams.value.storeyMax ? Number(searchParams.value.storeyMax) : 10,
  propertySizeMin: searchParams.value.propertySizeMin ? Number(searchParams.value.propertySizeMin) : null,
  wgType: searchParams.value.wgType ? searchParams.value.wgType.split(',').map(String) : [],
  requireImages: searchParams.value.requireImages || '0',
});

const meta = ref({
  city: '',
  areaName: '',
  title: t('meta.title.default'),
  description: t('meta.description.default'),
  totalCount: 0,
});

const { data } = await useAsyncData<MatchesResponse>(
  'fetchData',
  () =>
    fetchSearchData({
      filters: filters.value,
      page: Number(queryParams.page) || 1,
      order: String(queryParams.order) || 'standard',
      perPage: Number(queryParams.perPage) || 10,
    }) as Promise<MatchesResponse>,
);

if (data.value) {
  meta.value.totalCount = data.value.totalCount;
  housingCards.value = data.value.offers || [];
  searchCount.value = data.value.totalCount;
  pageCount.value = Math.ceil(searchCount.value / housingsPerPage.value);
}

const { data: locations } = await useAsyncData('fetchLocations', async () => {
  const response = await locationStore.fetchLocations({
    country: 'de',
    query: '',
    cities: filters.value.locationIds.cities,
    areas: filters.value.locationIds.areas,
  });
  return response as LocationRegion[];
});

if (locations.value) {
  if (filters.value.locationIds.areas.length > 0) {
    const areaAndCity = findAreaAndCityById(filters.value.locationIds.areas[0]);
    if (areaAndCity) {
      meta.value.city = areaAndCity.cityName;
      meta.value.areaName = areaAndCity.areaName;
    }
  }
}


const metaLinks = computed(() => {
  const url = `${config.public.seo.siteUrl}${canonicalUrl.value}`;

  const links = [
    {
      rel: 'canonical',
      href: url,
    },
  ];

  if (currentPage.value > 1) {
    links.push({
      rel: 'prev',
      href: addQueryParamToUrls(url, 'page', String(currentPage.value - 1)),
    });
  }

  if (currentPage.value < pageCount.value) {
    links.push({
      rel: 'next',
      href: addQueryParamToUrls(url, 'page', String(currentPage.value + 1)),
    });
  }

  return links;
});

const buttonDisabled = computed(() => {
  return filters.value.locationIds.cities.length !== 0 || filters.value.locationIds.areas.length !== 0 || filters.value.locationIds.regions.length !== 0;
});

const shouldShowBadgeList = computed(() => {
  const { properties, radius, storeyMin, storeyMax } = filters.value;
  return properties.length > 0 || (radius !== null && radius > 0) || (storeyMin !== null && storeyMin > -1) || (storeyMax !== null && storeyMax < 10);
});

const toggleMobileFilter = ref(false);
function capitalizeFirstLetter(string: string): string {
  if (!string) return '';
  return string
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function findAreaAndCityById(areaId: number): { areaName: string; cityName: string } | null {
  for (const region of locations.value) {
    for (const city of region.cities) {
      const area = city.areas.find((a) => (a as LocationArea).id === areaId) as LocationArea;
      if (area) {
        return {
          areaName: area.name,
          cityName: city.name,
        };
      }
    }
  }
  return null;
}

function setMeta() {
  if(cityPage) {
    meta.value.city = capitalizeFirstLetter(cityPage.name);
    meta.value.title = t('meta.title.city', { count: meta.value.totalCount, city: meta.value.city, apartment: t('apartment', meta.value.totalCount) });
    meta.value.description = t('meta.description.city', { city: meta.value.city });
  }

  if(meta.value.areaName) {
    meta.value.title = t('meta.title.area', { count: meta.value.totalCount, city: meta.value.city, area: meta.value.areaName, apartment: t('apartment', meta.value.totalCount) });
    meta.value.description = t('meta.description.area', { city: meta.value.city, area: meta.value.areaName });
}

}

function resetFilters() {
  filters.value = {
    ...filters.value,
    rentMax: null,
    sizeMin: null,
    roomsMin: 1,
    housingType: 1,
    housingKind: 1,
    housingTypeId: null,
    properties: [],
    housingProperties: [],
    radius: null,
    storeyMin: null,
    storeyMax: null,
    propertySizeMin: null,
    wgType: [],
    requireImages: '0',
  };
}

async function changePage(page: number, order?: string, perPage?: number): Promise<void> {
  if (typeof order !== 'string') order = housingsOrder.value;
  if (typeof perPage !== 'number') perPage = housingsPerPage.value;
  currentPage.value = page;
  housingsOrder.value = order;
  housingCards.value = [];

  window.scrollTo(0, window.scrollY > filtersOffset.value ? filtersOffset.value : 0);

  loading.value = true;

  try {
    const data = await fetchSearchData({ filters: filters.value, page, order, perPage });
    searchCount.value = data.totalCount;
    housingCards.value = data.offers || [];
    pageCount.value = Math.ceil(searchCount.value / data.perPage);

    const query = {
      page: currentPage.value.toString(),
      order: housingsOrder.value,
      ...getFiltersQuery(filters.value),
    };

    const queryString = createQueryString(query);

    let city = '';
    const dataLocation: Location[] = locationStore.getLocationsByIdsFlat(filters.value.locationIds);
    if (dataLocation.length > 0) {
      const locationData = dataLocation[0];
      const countryCode = locale.value;
      if (!locationData.hasOwnProperty('cityId')) {
        city = toSeoFriendly(locationData.name, countryCode);
      } else {
        city = toSeoFriendly(locationData.city.name, countryCode);
      }
    }

    const housingType = filters.value.housingType ?? 1;
    const housingKind = filters.value.housingKind ?? 1;
    const newHousingTypeText = reverseHousingTypeMap[housingType].replace('*', '-');
    const newHousingKindText = reverseHousingKindMap[housingKind];

    if (newHousingTypeText && newHousingKindText) {
      const newUrl = `/suche/${newHousingTypeText}-${newHousingKindText}/${city}`.toLowerCase();
      if (newUrl !== location.pathname) {
        navigateTo(`${newUrl}?${queryString}`);
      }
    }
    getLocationsText();
  } catch (e) {
    console.log(e);
  } finally {
    loading.value = false;
  }
}

function onScroll() {
  if (filtersElement.value) {
    const rect = filtersElement.value.getBoundingClientRect();
    filtersOffset.value = window.pageYOffset > rect.top ? rect.height : 0;
  }
}

async function applyFilters(newFilters: Filters) {
  filters.value = newFilters;
}

function createQueryString(params: QueryParams): string {
  const searchParams = new URLSearchParams();

  for (const key in params) {
    const value = params[key];
    if (value !== null && value !== undefined) {
      if (typeof value === 'object' && !Array.isArray(value)) {
        const nestedObject = value as Record<string, QueryParamValue>;

        for (const subKey in nestedObject) {
          const subValue = nestedObject[subKey];

          if (subValue !== null && subValue !== undefined) {
            if (Array.isArray(subValue)) {
              (subValue as (string | number)[]).forEach((val) => {
                console.log('subKey', subKey, val);
                searchParams.append(`${key}[${subKey}]`, val.toString());
              });
            } else {
              searchParams.append(`${key}[${subKey}]`, String(subValue));
            }
          }
        }
      } else if (Array.isArray(value)) {
        (value as (string | number)[]).forEach((val) => {
          searchParams.append(key, val.toString());
        });
      } else {
        searchParams.append(key, String(value));
      }
    }
  }
  return decodeURIComponent(searchParams.toString());
}

function updateWatching(watched: null | string, housingId: number) {
  const index = housingCards.value.findIndex((card) => card.housing.id === housingId);
  if (index === -1) return;
  housingCards.value[index].actions.watched = watched;
}

function resetRadius() {
  filters.value.radius = 0;
  changePage(currentPage.value);
}
function resetStorey() {
  filters.value.storeyMin = -1;
  filters.value.storeyMax = 10;
  changePage(currentPage.value);
}

function getLocationsText() {
  const locationsFlat = locationStore.getLocationsByIdsFlat(filters.value.locationIds);
  const locationBadges = getLocationBadgesFromLocations(locationsFlat);
  locationTexts.value = locationBadges.map((o) => (o.count === 1 ? o.text : `${o.text}`));
  getTitle();
}

function getTitle() {
  const main = [t('swap_apartments'), t('shared_rooms_swap'), t('houses_swap')];
  let location = '';

  if (locationTexts.value.length > 0) {
    location = t('in_locations', { locations: locationTexts.value.join(', ') });
  }

  const housingTypeValue = filters.value.housingType || housingType.value || 1;
  const housingTypeIndex = housingTypeValue - 1;

  const housingTypeText = main[housingTypeIndex] || main[0];

  if (location.trim().length > 27) {
    location = t('in_xxx_locations', { locations: locationTexts.value.length });
  }

  title.value = `${searchCount.value} ${housingTypeText} ${location}`;
}

function toggleMobileFilterHandler() {
  toggleMobileFilter.value = !toggleMobileFilter.value;
}

getTitle();
setMeta();


useSeoMeta({
  title: meta.value.title,
  description: meta.value.description,
  ogTitle: meta.value.title,
  ogDescription: meta.value.description,
});

onMounted(async () => {
  if (typeof route.query.page === 'string') currentPage.value = +route.query.page;
  if (typeof route.query.order === 'string') housingsOrder.value = route.query.order;

  onScroll();
  window.addEventListener('scroll', onScroll, false);
  window.addEventListener('resize', onScroll, false);

  useHead({
    link: () => metaLinks.value,
  });
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('resize', onScroll);
});

watch(
  () => route.meta,
  (newMeta) => {
    if (newMeta.cityPage) {
      route.meta.showMobileNavigation = true;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div id="TwFilters" ref="filtersElement" class="tw-view-navigation view--dashboard-matches tw-layout--disable-breakpoints">
    <div>
      <tw-search-bar-mobile
        v-if="!isDesktop"
        :filters="{ ...filters }"
        :loading="loading"
        :button-disabled="buttonDisabled"
        class="tw-layout--enable-breakpoints"
        @apply="applyFilters"
        @change-page="changePage(1)"
        @reset="resetFilters"
        :param-cities="filters.locationIds.cities"
        :param-areas="filters.locationIds.areas"
        :toggle-mobile-filter="toggleMobileFilter"
        @update:locations="getLocationsText"
      />
      <tw-search-bar
        v-else
        :filters="{ ...filters }"
        :loading="loading"
        :button-disabled="buttonDisabled"
        class="tw-layout--enable-breakpoints"
        @apply="applyFilters"
        @change-page="changePage(1)"
        @update:locations="getLocationsText"
        :param-cities="filters.locationIds.cities"
        :param-areas="filters.locationIds.areas"
      />
      <div v-if="shouldShowBadgeList && baseStore.state.properties.length > 0 && isDesktop" style="margin: 0 0 1rem; max-width: 1140px">
        <tw-filter-housing-properties-badge-list
          :value="filters.properties"
          @update:value="filters.properties = $event"
          :radius="filters.radius"
          @reset-radius="resetRadius"
          :storeyMin="filters.storeyMin"
          :storeyMax="filters.storeyMax"
          @reset-storey="resetStorey"
        />
      </div>
    </div>
  </div>
  <div class="tw-view view--dashboard-matches tw-layout--enable-breakpoints">
    <header>
      <h1>{{ title }}</h1>
    </header>

    <div v-if="loading" class="skeleton-main">
      <div class="-wrapper">
        <div><tw-skeleton :width="isDesktop ? '300px' : '100%'" height="200px" /></div>
        <div class="-info">
          <tw-skeleton width="300px" margin="10px 0" />
          <tw-skeleton width="300px" margin="10px 0" />
          <tw-skeleton width="300px" margin="10px 0" />
          <div class="-tags">
            <tw-skeleton width="90px" height="30px" margin="0.25rem 0.25rem 0.25rem 0" />
            <tw-skeleton width="90px" height="30px" margin="0.25rem" />
            <tw-skeleton width="90px" height="30px" margin="0.25rem" />
            <tw-skeleton width="90px" height="30px" margin="0.25rem" />
          </div>
          <div class="-button">
            <tw-skeleton width="200px" height="44px" margin="0" />
          </div>
        </div>
      </div>
      <div class="-footer">
        <tw-skeleton width="300px" />
      </div>
      <div class="-footer-button">
        <tw-skeleton width="100%" height="44px" />
      </div>
    </div>

    <tw-search-card-list
      v-else
      :search-cards="housingCards"
      :search-count="searchCount"
      :searches="searches"
      :users="users"
      :housings-per-page="housingsPerPage"
      :order="housingsOrder"
      :page="currentPage"
      :page-count="pageCount"
      :show-pagination="!loading"
      @update-watching="updateWatching($event.watched, $event.housingId)"
      @update:page="changePage($event)"
      @update:order="changePage(1, $event)"
      @trigger:toggle-mobile-filter="toggleMobileFilterHandler"
    >
      <template #count>
        {{ t('you_have') }}
        <Icon name="line-md:loading-twotone-loop" v-if="loading" />
        <strong v-else>{{ searchCount }}</strong>
        {{ t('results') }}
      </template>
    </tw-search-card-list>
  </div>
</template>

<style lang="scss" scoped>
@import 'assets/styles/tw-variables';
@import '@/assets/styles/tw-mixins.scss';

h1 {
  font-size: 1.75rem;
  line-height: 2.25rem;
  margin: 0;
  @include tw-is-desktop {
    font-size: 2.25rem;
  }
}

:deep(.tw-icon) {
  height: 1em !important;
}

.filter--housing-properties {
  display: flex;
  justify-content: center;
}

.tw-view {
  display: flex;
  flex-flow: column nowrap;
  gap: 1em;
}

#TwFilters {
  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #f5f5f5;
    box-shadow: 0 0.2em 0.2em 0 $lightGray;
    border-top: 0.1em solid $lightGray;
  }
}

.tw-widget.widget--premium-teaser {
  margin: 2em auto;
}

.tw-housing-card-list {
  .no-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5em;
    margin: 4em auto;

    .tw-icon {
      font-size: 5em;
      color: $gray;
    }

    .message {
      font-size: 1.1em;
      font-weight: bold;
      text-align: center;
    }

    p {
      margin: 0;
    }

    .tip {
      font-size: 0.9em;
      text-align: center;
      color: $gray;

      .tw-button.action--activate-housing {
        margin: 0;
        padding: 0;
      }
    }
  }
}

.tw-info-box {
  transition:
    opacity 0.1s 0.1s ease-in-out,
    transform 0.1s 0.1s ease-in-out,
    height 0.1s 0.1s ease-in-out;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
    height: 0;
    transform: scaleY(0);
  }

  &.v-enter-to,
  &.v-leave-from {
    height: 5em;
  }
}
.skeleton-main {
  width: 100%;
  background-color: $white;
  border-radius: 0.22rem;
  border: 0.1rem solid $lightGray;
  box-shadow: 0 0.2em 0.2em 0 $lightGray;
  .-wrapper {
    display: flex;
    flex-direction: column;
    padding: 1rem;
    gap: 0.5rem;
    @include tw-is-desktop {
      flex-direction: row;
    }
  }
  .-info {
    width: 100%;
  }
  .-tags {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }
  .-button {
    display: none;
    @include tw-is-desktop {
      display: flex;
      justify-content: flex-end;
    }
  }
  .-footer {
    padding: 1rem;
    border-top: 1px solid #dcdcdc;
  }
  .-footer-button {
    padding: 1rem;
    border-top: 1px solid #dcdcdc;
    @include tw-is-desktop {
      display: none;
    }
  }
}
</style>

<i18n lang="json">
{
  "de": {
    "apartment": "Tauschwohnung | Tauschwohnungen",
    "swap_apartments": "Tauschwohnungen",
    "shared_rooms_swap": "WG-Zimmer zum Tausch",
    "houses_swap": "Häuser zum Tausch",
    "in_xxx_locations": " in {locations} Orten",
    "in_locations": " in {locations}",
    "you_have": "Du hast",
    "results": "Ergebnisse",
    "housing_types": {
      "apartment": "Wohnung",
      "shared_room": "WGZimmer",
      "house": "Haus"
    },
    "housing_kind": {
      "rent": "Mieten",
      "buy": "Kaufen"
    },
    "meta": {
      "title": {
        "default": "Dauerhafter Wohnungstausch - Tauschwohnung",
        "city": "{count} {apartment} in {city}",
        "area": "{count} {apartment} in {city} - {area}"
      },
      "description": {
        "default": "Tauschwohnung gesucht? Auf tauschwohnung.com – der Nr. 1 für Wohnungstausch - findest du die größte Auswahl an Tauschwohnungen.",
        "city": "Tauschwohnung {city} gesucht? Auf tauschwohnung.com – der Nr. 1 für Wohnungstausch - findest du die größte Auswahl an Tauschwohnungen.",
        "area": "Entdecke aktuelle und günstige Tauschwohnungen zur Miete in {city} - {area} und starte noch heute mit deinem Wohnungstausch."
      }
    }
  },
  "en": {
    "apartment": "Apartment | Apartments",
    "swap_apartments": "Swap apartments",
    "shared_rooms_swap": "Shared rooms swap",
    "houses_swap": "Houses swap",
    "in_xxx_locations": " in {locations} locations",
    "in_locations": " in {locations}",
    "you_have": "You have",
    "results": "results",
    "housing_types": {
      "apartment": "Apartment",
      "shared_room": "Shared room",
      "house": "House"
    },
    "housing_kind": {
      "rent": "Rent",
      "buy": "Buy"
    },
    "meta": {
      "title": {
        "default": "Dauerhafter Wohnungstausch - Tauschwohnung",
        "city": "{count} {apartment} in {city}",
        "area": "{count} {apartment} in {city} - {area}"
      },
      "description": {
        "default": "Looking for a swap apartment? At tauschwohnung.com - the No. 1 for apartment exchange - you will find the largest selection of swap apartments.",
        "city": "Looking for a swap apartment in {city}? At tauschwohnung.com - the No. 1 for apartment exchange - you will find the largest selection of swap apartments.",
        "area": "Discover current and affordable swap apartments for rent in {city} - {area} and start your apartment exchange today."
      }
    }
  }
}
</i18n>
